import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
  BidRateModHistory,
  Calendar,
  CalendarService,
  CalendarType,
  CommonTransformationService,
  ContractModTypeName,
  DefaultRowsDT,
  Format,
  GrowlType,
  PrimeNgConfig,
  RateService,
  RateSummary,
  RegExp4DigitDecimal,
  TableColumn,
  TableFormat,
  Transaction,
  Validation
} from "@app/shared";
import {plainToClass} from "class-transformer";
import {SelectItem} from "primeng/api";
import {GrowlService} from "@app/core";
import {OverlayPanel} from "primeng/overlaypanel";

@Component({
  selector: 'app-rate-summary',
  templateUrl: './rate-summary.component.html',
  styleUrls: ['./rate-summary.component.css']
})
export class RateSummaryComponent implements OnInit, OnChanges {

  @Input() transaction: Transaction;
  @Input() refId: number;
  @Input() referenceIdentifier: string;
  @Input() requireMod: boolean;
  @Input() editable: boolean;


  ContractModTypeName: typeof ContractModTypeName = ContractModTypeName;
  defaultRowsDT = DefaultRowsDT;
  Format: typeof Format = Format;
  PrimeNgConfig = PrimeNgConfig;
  RegExp4DigitDecimal = RegExp4DigitDecimal;
  Transaction: typeof Transaction = Transaction;
  Validation: typeof Validation = Validation;


  selectedYear: string;
  selectedAccountingPeriodId: number;
  tableColumns: any;

  fiscalYearDropdown: SelectItem[] = [];
  accountingPeriodDropdown: SelectItem[] = [];
  entityModDropdown: SelectItem[] = [];


  selectedModId: number;
  editBidRates: boolean;
  rateSummaries: RateSummary [] = [];
  accountingPeriodsForYear: Calendar [] = [];
  currentAccountingPeriod: Calendar;

  // ModHistory properties
  selectedRateHistories: BidRateModHistory [] = [];
  rateHistoryTableColumns: TableColumn[];


  constructor(private rateService: RateService, private calendarService: CalendarService, private transform: CommonTransformationService, private growl: GrowlService) { }

  ngOnInit() {
    this.buildMenu();
    this.getYearDropDown();
    this.getEntityModDropdown();
    this.getRateSummaries();
    this.getCurrentPeriod();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getRateSummaries();
  }

  onClickRefresh() {
    this.getRateSummaries();
  }

  onClickEditRates() {
    this.editBidRates = true;
  }

  onChangeYear(selectedYear: string) {
    this.getRateSummaries();
    if (selectedYear) {
      this.calendarService.getCalendarsForYear(CalendarType.Accounting, selectedYear).subscribe(res => {
        this.accountingPeriodsForYear = plainToClass(Calendar, res.list);
        this.accountingPeriodDropdown = this.transform.convertCalendarToPeriodDropdown(this.accountingPeriodsForYear);
        // sort based on period which in this case is value
        this.accountingPeriodDropdown.sort((a, b) => a.value.period < b.value.period ? -1 : a.value.period > b.value.period ? 1 : 0);
      });
    }
  }

  onChangeAccountingPeriod() {
    this.getRateSummaries();
  }

  onClickInitializeBidRate(rate: RateSummary){
    rate.bidRate = 0;
    rate.bidRateAsPercentage = 0;
    rate.transaction = this.transaction;
    rate.refId = this.refId;
  }

  onClickCancelEditRates() {
    this.editBidRates = false;
  }

  clearRateHistory() {
    this.selectedRateHistories = [];
  }

  showBidRateHistory(bidRateId: number, $event, op: OverlayPanel, target) {
    if (bidRateId != null) {

      this.rateService.getBidRateModHistory(bidRateId).subscribe(res => {
        this.selectedRateHistories = plainToClass(BidRateModHistory, res);
        op.toggle(event, target);
      });
    }
  }

  onClickSaveRates() {
    console.log(this.rateSummaries);
    this.rateSummaries.forEach(rate => {
      if (this.selectedModId) {
        rate.contractModId = this.selectedModId;
      }
      if (this.selectedAccountingPeriodId) {
        rate.accountingPeriodId = this.selectedAccountingPeriodId;
      }
    });
    this.rateService.saveOrUpdateBidRates(this.rateSummaries).subscribe(res => {
      this.rateSummaries = plainToClass(RateSummary, res);
      this.growl.add(GrowlType.Success, "Bid Rates have been updated successfully");
    });
  }

  onChangeBidRate(rate: RateSummary) {
    let percentageAsRate: number = (rate.bidRateAsPercentage / 100);
    rate.bidRate = percentageAsRate;
  }

  private getYearDropDown() {
    this.calendarService.getCalendarYearsAsDropdown(CalendarType.Accounting).subscribe(res => {
      this.fiscalYearDropdown = res;
    });
  }

  private getCurrentPeriod(){
    this.calendarService.getCurrentPeriod(CalendarType.Accounting).subscribe(res => {
      this.currentAccountingPeriod = plainToClass(Calendar, res as Calendar);
    });
  }

  private getEntityModDropdown() {
    if (this.transaction == Transaction.Contract) {
      this.rateService.getContractMods(this.refId).subscribe(res => {
        this.entityModDropdown = this.transform.convertToDropDown("modNumber", res.list, "id");
      });
    } else if(this.transaction == Transaction.ContractBid){
      this.rateService.getContractModsForBid(this.refId).subscribe(res => {
        this.entityModDropdown = this.transform.convertToDropDown("modNumber", res.list, "id");
      });
    }
  }


  private buildMenu() {


    this.tableColumns = [
      {field: 'costPoolName', header: 'Cost Pool'},
      {field: 'poolType', header: 'Pool Type'},
      {field: 'provisionalRate', header: 'Provisional Rate'},
      {field: 'actualRate', header: 'Actual Rate'},
      {field: 'bidRate', header: 'Bid Rate', editable: 'pEditableColumn'}

    ];

    this.rateHistoryTableColumns = [
      {field: 'modNumber', header: 'Mod Number'},
      {field: 'modEffectiveOn', header: 'Mod Effective On', format: TableFormat.Date},
      {field: 'bidRate', header: 'Rate', format: TableFormat.Percentage},
      {field: 'accountingPeriodAsString', header: 'Accounting Period'},
    ];

  }

  private getRateSummaries() {
    let accountingPeriodId: number;
    if (this.selectedAccountingPeriodId) {
      accountingPeriodId = this.selectedAccountingPeriodId;
    } else if (this.currentAccountingPeriod) {
      accountingPeriodId = this.currentAccountingPeriod.id;
    }
    this.rateService.getRateSummaries(this.transaction, this.refId, this.selectedYear, accountingPeriodId, true).subscribe(res => {
      this.rateSummaries = plainToClass(RateSummary, res);
    });
  }

}
